export type LiveFindingCard = {
  title: string;
  family: string;
  ruleId: string;
  path: string;
  lineStart: number;
};

export type ScanLiveUiEvent = {
  type?: string;
  at?: string;
  message?: string;
  headline?: string;
  status?: string;
  files?: string[];
  finding?: LiveFindingCard;
};

export type ScanLiveUiState = {
  logs: string[];
  files: string[];
  headline: string | null;
  findings: LiveFindingCard[];
  lastHeardAt: string | null;
  phaseStartedAt: string | null;
};

export const EMPTY_SCAN_LIVE_UI: ScanLiveUiState = {
  logs: [],
  files: [],
  headline: null,
  findings: [],
  lastHeardAt: null,
  phaseStartedAt: null,
};

function findingKey(finding: LiveFindingCard): string {
  return `${finding.ruleId}|${finding.path}|${finding.lineStart}`;
}

export function publicScannerLabel(text: string): string {
  return text
    .replace(
      /Codex Security primary pass on snapshot files/gi,
      "AI Security Scanner is reviewing snapshot files",
    )
    .replace(/Codex Security primary pass/gi, "AI Security Scanner")
    .replace(/Host-authenticated Codex Security/gi, "The AI security scanner")
    .replace(/Codex Security/gi, "AI Security Scanner");
}

export function shortScanPath(path: string): string {
  const parts = path.split("/").filter(Boolean);
  if (parts.length <= 2) return path;
  return `…/${parts.slice(-2).join("/")}`;
}

export function liveStatusCopy(input: {
  headline: string | null;
  status: string;
  fileCount: number;
  findingCount: number;
}): { title: string; body: string } {
  const headline = publicScannerLabel(input.headline ?? "");
  if (/AI Security Scanner|detecting/i.test(`${headline} ${input.status}`)) {
    return {
      title: headline || "AI Security Scanner is reviewing snapshot files",
      body:
        input.findingCount > 0
          ? `${input.findingCount} finding${input.findingCount === 1 ? "" : "s"} recorded so far. The AI pass is still running.`
          : `Reading ${input.fileCount || "the"} snapshot file${input.fileCount === 1 ? "" : "s"}. This pass often takes several minutes. Findings appear when it finishes — the file names below are the snapshot, not a live ticker.`,
    };
  }
  if (input.status === "snapshotting") {
    return {
      title: "Freezing a copy of the repository",
      body: "Hashing files and applying size and path rules. Nothing is executed.",
    };
  }
  if (input.status === "validating") {
    return {
      title: "Checking evidence against the snapshot",
      body: "Replay and challenge are running. Detector output is not yet final truth.",
    };
  }
  if (input.status === "indexing") {
    return {
      title: "Saving governed findings",
      body: "Indexing tenant-scoped results and coverage.",
    };
  }
  if (input.status === "queued") {
    return {
      title: "Waiting to start",
      body: "The scan is queued behind the coordinator.",
    };
  }
  return {
    title: headline || "Scan in progress",
    body: "Live events stream into this panel. Coverage is not complete until the run finishes.",
  };
}

export function formatScanDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${seconds.toString().padStart(2, "0")}s`;
}

function withHeard(state: ScanLiveUiState, at?: string): ScanLiveUiState {
  return { ...state, lastHeardAt: at ?? new Date().toISOString() };
}

export function applyScanLiveEvent(
  state: ScanLiveUiState,
  event: ScanLiveUiEvent,
): ScanLiveUiState {
  const at = typeof event.at === "string" ? event.at : undefined;
  if (event.type === "heartbeat" || event.type === "hello") {
    const heard = withHeard(state, at);
    return {
      ...heard,
      phaseStartedAt: heard.phaseStartedAt ?? at ?? new Date().toISOString(),
    };
  }
  if (event.type === "log" && event.message) {
    return withHeard({ ...state, logs: [...state.logs.slice(-48), event.message] }, at);
  }
  if (event.type === "files" && Array.isArray(event.files)) {
    return withHeard({ ...state, files: event.files }, at);
  }
  if (event.type === "phase" && event.headline) {
    return withHeard(
      {
        ...state,
        headline: event.headline,
        phaseStartedAt: state.phaseStartedAt ?? at ?? new Date().toISOString(),
      },
      at,
    );
  }
  if (event.type === "finding" && event.finding) {
    const card = event.finding;
    if (state.findings.some((item) => findingKey(item) === findingKey(card))) {
      return withHeard(state, at);
    }
    return withHeard({ ...state, findings: [...state.findings, card].slice(-24) }, at);
  }
  return event.type ? withHeard(state, at) : state;
}
